<template>
  <article
    class="artifact-card"
    :class="{ 'artifact-card--analysing': analysisStatus === 'analysing' }"
    role="button"
    tabindex="0"
    :title="artifact.title"
    data-testid="artifact-card"
    @click="emit('click')"
    @keydown.enter.prevent="emit('click')"
    @keydown.space.prevent="emit('click')"
  >
    <div class="artifact-card__preview" :class="`artifact-card__preview--${artifact.type}`">
      <img
        v-if="artifact.type === 'screenshot' && artifact.url"
        :src="artifact.url"
        :alt="artifact.title"
        class="artifact-card__image"
      />
      <KdsIcon
        v-else
        :name="iconFor(artifact.type)"
        :size="32"
        class="artifact-card__icon"
      />

      <!-- Analysis status badge (top-left, CA-18/CA-19/CA-20/CA-21) — only shown when prop is provided.
           D1: reuses the KdsBadge primitive (Signal/ok/danger tones), no hardcoded colors.
           D2: analysing uses the Signal pulse dot inherited from KdsChip, not a spinner. -->
      <Transition name="badge-swap">
        <span
          v-if="analysisStatus && analysisStatus !== 'pending'"
          key="status-badge"
          class="artifact-card__status-badge"
          :class="`artifact-card__status-badge--${analysisStatus}`"
          :aria-label="t('projects.artifacts.status.statusAria', { status: t(`projects.artifacts.status.${analysisStatus}`) })"
          :title="statusTitle"
        >
          <KdsBadge :tone="statusTone">
            <!-- D2: analysing → Signal pulse dot (CA-19) -->
            <span
              v-if="analysisStatus === 'analysing'"
              class="artifact-card__status-dot kds-pulse-dot"
              aria-hidden="true"
            />
            <!-- analysed / error → outline icon -->
            <KdsIcon
              v-else
              :name="statusIcon"
              :size="12"
              class="artifact-card__status-icon"
              aria-hidden="true"
            />
            {{ t(`projects.artifacts.status.${analysisStatus}`) }}
          </KdsBadge>
        </span>
      </Transition>

      <!-- Overlay actions: remove + relaunch (hover on pointer devices, always visible on touch) -->
      <div class="artifact-card__overlay">
        <button
          class="artifact-card__remove"
          type="button"
          :aria-label="`Remove ${artifact.title}`"
          @click.stop="emit('remove')"
          @keydown.enter.stop.prevent="emit('remove')"
          @keydown.space.stop.prevent="emit('remove')"
        >
          <KdsIcon name="trash-2" :size="18" />
        </button>
        <!-- Relaunch analysis action (CA-17) — only when analysisStatus prop is provided -->
        <button
          v-if="analysisStatus !== undefined"
          class="artifact-card__relaunch"
          type="button"
          :aria-label="t('projects.brief.relaunchArtifact')"
          :disabled="relaunchDisabled"
          :title="relaunchButtonTitle"
          @click.stop="emit('relaunch')"
          @keydown.enter.stop.prevent="emit('relaunch')"
          @keydown.space.stop.prevent="emit('relaunch')"
        >
          <KdsIcon name="refresh-cw" :size="18" />
        </button>
      </div>
    </div>
    <header class="artifact-card__head">
      <span class="eyebrow font-mono">{{ t(`projects.artifactTypes.${artifact.type}`) }}</span>
      <h3 class="artifact-card__title">{{ artifact.title }}</h3>
    </header>
    <p class="artifact-card__time font-mono">{{ relativeTime }}</p>
  </article>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRelativeTime } from '@/composables/useRelativeTime';
import { crawlErrorI18nKey } from '@/utils/crawlErrors';
import { KdsIcon, KdsBadge } from '@/kds';
import type { KdsIconName, KdsBadgeTone } from '@/kds/types';
import type { ProjectArtifact } from '@/types/project';
import type { ArtifactAnalysisStatus } from '@/types/projectAnalysis';

const { t } = useI18n();

const props = defineProps<{
  artifact: ProjectArtifact;
  /**
   * Optional analysis status overlay. When omitted, no badge or relaunch
   * button is rendered (retro-compatible default, D3).
   */
  analysisStatus?: ArtifactAnalysisStatus;
  /**
   * Traversing prop (M7, CA-21) — why the relaunch button must stay
   * disabled for a reason outside this card's own status: an analysis is
   * running elsewhere (brief-level lock) or the brief has unsaved edits.
   * Combines with the card's own `analysisStatus === 'analysing'` check.
   */
  relaunchDisabledReason?: 'analysing' | 'unsaved' | null;
}>();

const emit = defineEmits<{
  click: [];
  remove: [];
  /** Emitted when the user requests re-analysis of this artifact (CA-17). */
  relaunch: [];
}>();

const { relative } = useRelativeTime(toRef(props.artifact, 'createdAt'));
const relativeTime = computed(() => relative.value);

const iconFor = (type: ProjectArtifact['type']): KdsIconName => {
  switch (type) {
    case 'brief':
    case 'transcript':
      return 'file-text';
    case 'video':
      return 'play-circle';
    case 'link':
      return 'link';
    case 'screenshot':
    default:
      return 'image';
  }
};

/** Icon name for the analysed / error badge (CA-19/CA-21). analysing uses a pulse dot (D2). */
const statusIcon = computed<KdsIconName>(() => {
  switch (props.analysisStatus) {
    case 'analysed': return 'check';
    case 'error': return 'circle-alert';
    default: return 'clock';
  }
});

/** Maps the analysis status to a KdsBadge tone (D1): analysing=live, analysed=ok, error=danger. */
const statusTone = computed<KdsBadgeTone>(() => {
  switch (props.analysisStatus) {
    case 'analysing': return 'live';
    case 'analysed': return 'ok';
    case 'error': return 'danger';
    default: return 'muted';
  }
});

/**
 * Tooltip for the badge. On error, translates the persisted crawl code
 * (`crawl_timeout`…) — a raw `analysis_error` value is never rendered, machine
 * code or driver message alike; anything untranslatable falls back to the
 * static status label.
 */
const statusTitle = computed<string | undefined>(() => {
  if (props.analysisStatus !== 'error') return undefined;
  return t(crawlErrorI18nKey(props.artifact.analysisError) ?? 'projects.artifacts.status.error');
});

/**
 * D9 — the card whose own analysis is running keeps its relaunch button
 * enabled: an artifact stuck in `analysing` (dead function, lost cold start)
 * locks brief editing and disables every other relaunch, so this button is
 * the only way out of the dead end. Any other card stays governed by the
 * traversing reason.
 */
const relaunchDisabled = computed<boolean>(() => {
  if (props.analysisStatus === 'analysing') return false;
  return !!props.relaunchDisabledReason;
});

/**
 * Names the exact cause when the relaunch button is disabled by the
 * traversing reason (M7, CA-21/CA-24) — never combined, and never shown on a
 * button that stays enabled (D9).
 */
const relaunchButtonTitle = computed<string | undefined>(() => {
  if (!relaunchDisabled.value) return undefined;
  if (props.relaunchDisabledReason === 'analysing') return t('projects.brief.relaunchDialog.lockedHint');
  if (props.relaunchDisabledReason === 'unsaved') return t('projects.brief.relaunchArtifactDialog.unsavedHint');
  return undefined;
});
</script>

<style scoped lang="scss">
.artifact-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--paper);
  border: var(--border);
  border-radius: var(--r-4);
  box-shadow: var(--shadow-1);
  cursor: pointer;
  outline: none;
  transition: box-shadow var(--d-fast) var(--ease-out), border-color var(--d-fast) var(--ease-out);

  &:hover,
  &:focus-visible {
    border-color: var(--mist-2);
    box-shadow: var(--shadow-2);
  }

  &:focus-visible {
    box-shadow: var(--shadow-focus);
  }

  /* Analysing: highlighted border (CA-20) */
  &--analysing {
    border-color: var(--signal-press, var(--signal));
  }

  &__preview {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 16 / 9;
    border-radius: var(--r-3);
    background: var(--paper-2);
    overflow: hidden;
    color: var(--stone);
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__icon { color: var(--ink); }

  /* Status badge — top-left overlay positioner (CA-18/CA-19/CA-20/CA-21).
     Colors come from the nested KdsBadge primitive (D1) — no hardcoded values. */
  &__status-badge {
    position: absolute;
    top: var(--space-2);
    left: var(--space-2);
    display: inline-flex;
    pointer-events: none;
    z-index: 1;
  }

  /* Signal pulse dot for the analysing badge (D2) — matches KdsChip live dot */
  &__status-dot {
    width: 6px;
    height: 6px;
    margin-right: var(--space-1);
    border-radius: var(--r-pill);
    background: var(--signal);
    flex-shrink: 0;
  }

  &__status-icon {
    margin-right: var(--space-1);
    flex-shrink: 0;
  }

  /* Overlay container for remove + relaunch buttons */
  &__overlay {
    position: absolute;
    top: var(--space-2);
    right: var(--space-2);
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    opacity: 0;
    transition: opacity var(--d-fast) var(--ease-out);

    @media (hover: none) {
      opacity: 1;
    }
  }

  &:hover &__overlay,
  &:focus-within &__overlay {
    @media (hover: hover) {
      opacity: 1;
    }
  }

  /* Shared overlay button styles */
  &__remove,
  &__relaunch {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background: var(--surface-overlay);
    border: none;
    border-radius: var(--r-2);
    color: var(--ink-overlay-strong);
    cursor: pointer;
    transition: color var(--d-fast) var(--ease-out);

    &:hover { color: var(--ink); }

    &:focus-visible {
      outline: 2px solid var(--paper);
      outline-offset: 2px;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &__head {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  &__title {
    margin: 0;
    font-size: var(--fs-body);
    color: var(--ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__time {
    margin: 0;
    color: var(--stone);
    font-size: var(--fs-mono);
  }
}

/* Badge crossfade transition (CA-21) */
.badge-swap-enter-active,
.badge-swap-leave-active {
  transition: opacity var(--d-fast) var(--ease-out), transform var(--d-fast) var(--ease-out);
}
.badge-swap-enter-from,
.badge-swap-leave-to {
  opacity: 0;
  transform: scale(0.85);
}
</style>
